import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react';
import {
  DEVICE_SCOPE_CATEGORIES,
  REPAIR_CATEGORIES,
  bulkUpsertRepairServicePricing,
  deleteRepairServicePricing,
  listRepairServicePricing,
  listRepairServices,
  previewRepairServiceScope,
  updateRepairService,
  upsertRepairServicePricing,
  type ServiceScopeType,
} from '@/lib/api/repair-services.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type PanelSelection = 'info' | string;

export function RepairServiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<PanelSelection>('info');
  const [addOpen, setAddOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(true);
  const [addMakeId, setAddMakeId] = useState('');
  const [addModelId, setAddModelId] = useState('');
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [addPrice, setAddPrice] = useState('');
  const [addSalePrice, setAddSalePrice] = useState('');
  const [addMinutes, setAddMinutes] = useState('');
  const [addWarranty, setAddWarranty] = useState('90');

  const servicesQuery = useQuery({
    queryKey: ['repair-services-catalog'],
    queryFn: () => listRepairServices({ includeInactive: true }),
  });
  const service = servicesQuery.data?.find((s) => s.id === id);

  const pricingQuery = useQuery({
    queryKey: ['repair-service-pricing', id],
    queryFn: () => listRepairServicePricing(id!),
    enabled: Boolean(id),
  });

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });
  const modelsQuery = useQuery({
    queryKey: ['device-models', addMakeId],
    queryFn: () => listDeviceModels(addMakeId ? { makeId: addMakeId } : undefined),
    enabled: Boolean(addMakeId),
  });

  const activePricing = useMemo(
    () => (pricingQuery.data ?? []).filter((p) => p.active && p.modelId),
    [pricingQuery.data],
  );

  const selectedPricing = useMemo(
    () => activePricing.find((p) => p.id === selected),
    [activePricing, selected],
  );

  const [infoName, setInfoName] = useState('');
  const [infoCategory, setInfoCategory] = useState('screen');
  const [infoDefaultPrice, setInfoDefaultPrice] = useState('');
  const [infoSortOrder, setInfoSortOrder] = useState('999');
  const [infoDisplayPos, setInfoDisplayPos] = useState(true);
  const [infoDisplayPortal, setInfoDisplayPortal] = useState(true);
  const [infoDisplayWidget, setInfoDisplayWidget] = useState(true);
  const [infoLoyalty, setInfoLoyalty] = useState('inherit');
  const [infoLoyaltyRate, setInfoLoyaltyRate] = useState('');
  const [infoActive, setInfoActive] = useState(true);

  const [scopeType, setScopeType] = useState<ServiceScopeType>('per_model');
  const [scopeCategories, setScopeCategories] = useState<string[]>([]);
  const [scopeMakeIds, setScopeMakeIds] = useState<string[]>([]);
  const [scopeExcludedModelIds, setScopeExcludedModelIds] = useState<string[]>([]);
  const [flatPrice, setFlatPrice] = useState('');
  const [flatMinutes, setFlatMinutes] = useState('');
  const [flatWarranty, setFlatWarranty] = useState('');
  const [addScopeMakeId, setAddScopeMakeId] = useState('');
  const [excludeMakeId, setExcludeMakeId] = useState('');
  const [excludeModelId, setExcludeModelId] = useState('');

  const [modelPrice, setModelPrice] = useState('');
  const [modelSalePrice, setModelSalePrice] = useState('');
  const [modelMinutes, setModelMinutes] = useState('');
  const [modelWarranty, setModelWarranty] = useState('');
  const [modelDescription, setModelDescription] = useState('');
  const [modelActive, setModelActive] = useState(true);

  const excludeModelsQuery = useQuery({
    queryKey: ['device-models', excludeMakeId],
    queryFn: () => listDeviceModels(excludeMakeId ? { makeId: excludeMakeId } : undefined),
    enabled: Boolean(excludeMakeId),
  });

  useEffect(() => {
    if (!service) return;
    setInfoName(service.name);
    setInfoCategory(service.categorySlug ?? 'other');
    setInfoDefaultPrice(service.defaultPrice ?? '');
    setInfoSortOrder(String(service.sortOrder ?? 999));
    setInfoDisplayPos(service.displayOnPos !== false);
    setInfoDisplayPortal(service.displayOnPortal !== false);
    setInfoDisplayWidget(service.displayOnWidget !== false);
    setInfoLoyalty(service.loyaltyOverride ?? 'inherit');
    setInfoLoyaltyRate(service.loyaltyEarnRate ?? '');
    setInfoActive(service.active !== false);
    setScopeType(service.scopeType ?? 'per_model');
    setScopeCategories(service.scopeCategories ?? []);
    setScopeMakeIds(service.scopeMakeIds ?? []);
    setScopeExcludedModelIds(service.scopeExcludedModelIds ?? []);
    setFlatPrice(service.flatPrice ?? '');
    setFlatMinutes(
      service.flatEstimatedMinutes != null ? String(service.flatEstimatedMinutes) : '',
    );
    setFlatWarranty(service.flatWarrantyDays != null ? String(service.flatWarrantyDays) : '');
  }, [service]);

  useEffect(() => {
    if (!selectedPricing) return;
    setModelPrice(selectedPricing.price);
    setModelSalePrice(selectedPricing.salePrice ?? '');
    setModelMinutes(selectedPricing.estimatedMinutes != null ? String(selectedPricing.estimatedMinutes) : '');
    setModelWarranty(selectedPricing.warrantyDays != null ? String(selectedPricing.warrantyDays) : '');
    setModelDescription(selectedPricing.description ?? '');
    setModelActive(selectedPricing.active);
  }, [selectedPricing]);

  const scopePreviewQuery = useQuery({
    queryKey: ['scope-preview', scopeType, scopeCategories, scopeMakeIds, scopeExcludedModelIds],
    queryFn: () =>
      previewRepairServiceScope({
        scopeType,
        scopeCategories,
        scopeMakeIds,
        scopeExcludedModelIds,
      }),
    enabled: scopeType !== 'per_model',
  });

  const saveInfoMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: infoName.trim(),
        categorySlug: infoCategory,
        defaultPrice: infoDefaultPrice || null,
        sortOrder: Number(infoSortOrder) || 999,
        displayOnPos: infoDisplayPos,
        displayOnPortal: infoDisplayPortal,
        displayOnWidget: infoDisplayWidget,
        loyaltyOverride: infoLoyalty,
        loyaltyEarnRate: infoLoyalty === 'custom_rate' ? infoLoyaltyRate || null : null,
        active: infoActive,
        scopeType,
        scopeCategories,
        scopeMakeIds,
        scopeExcludedModelIds,
      };
      if (scopeType === 'per_model') {
        body.flatPrice = null;
        body.flatEstimatedMinutes = null;
        body.flatWarrantyDays = null;
      } else {
        body.flatPrice = Number(flatPrice);
        body.flatEstimatedMinutes = flatMinutes ? Number(flatMinutes) : null;
        body.flatWarrantyDays = flatWarranty ? Number(flatWarranty) : null;
      }
      return updateRepairService(id!, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      toastSuccess('Service info saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const saveModelMut = useMutation({
    mutationFn: () => {
      if (!selectedPricing?.makeId) throw new Error('Missing make');
      return upsertRepairServicePricing(id!, {
        makeId: selectedPricing.makeId,
        modelId: selectedPricing.modelId ?? undefined,
        price: Number(modelPrice),
        salePrice: modelSalePrice ? Number(modelSalePrice) : undefined,
        estimatedMinutes: modelMinutes ? Number(modelMinutes) : undefined,
        warrantyDays: modelWarranty ? Number(modelWarranty) : undefined,
        description: modelDescription || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair-service-pricing', id] });
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      toastSuccess('Pricing saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const addPricingMut = useMutation({
    mutationFn: async () => {
      if (bulkMode) {
        if (!addMakeId || selectedModelIds.length === 0) throw new Error('Select models');
        return bulkUpsertRepairServicePricing(id!, {
          modelIds: selectedModelIds,
          price: Number(addPrice),
          salePrice: addSalePrice ? Number(addSalePrice) : undefined,
          estimatedMinutes: addMinutes ? Number(addMinutes) : undefined,
          warrantyDays: addWarranty ? Number(addWarranty) : undefined,
        });
      }
      if (!addMakeId) throw new Error('Select brand');
      return upsertRepairServicePricing(id!, {
        makeId: addMakeId,
        modelId: addModelId || undefined,
        price: Number(addPrice),
        salePrice: addSalePrice ? Number(addSalePrice) : undefined,
        estimatedMinutes: addMinutes ? Number(addMinutes) : undefined,
        warrantyDays: addWarranty ? Number(addWarranty) : undefined,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['repair-service-pricing', id] });
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      setAddOpen(false);
      setSelectedModelIds([]);
      setAddModelId('');
      toastSuccess(
        bulkMode && result && 'count' in result
          ? `Added pricing for ${result.count} models`
          : 'Model pricing added',
      );
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const removeModelMut = useMutation({
    mutationFn: (pricingId: string) => deleteRepairServicePricing(id!, pricingId),
    onSuccess: () => {
      setSelected('info');
      queryClient.invalidateQueries({ queryKey: ['repair-service-pricing', id] });
      queryClient.invalidateQueries({ queryKey: ['repair-services-catalog'] });
      toastSuccess('Model removed');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function toggleModel(modelId: string) {
    setSelectedModelIds((current) =>
      current.includes(modelId) ? current.filter((x) => x !== modelId) : [...current, modelId],
    );
  }

  function toggleCategory(value: string) {
    setScopeCategories((current) =>
      current.includes(value) ? current.filter((c) => c !== value) : [...current, value],
    );
  }

  const isPerModel = scopeType === 'per_model';
  const makeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of makesQuery.data ?? []) map.set(m.id, m.name);
    return map;
  }, [makesQuery.data]);

  if (!id) return null;
  if (servicesQuery.isLoading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!service) {
    return (
      <div className="p-8">
        <p className="text-slate-600">Service not found.</p>
        <Link to="/settings/repair-services" className="mt-2 inline-block text-primary">← Back</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/settings/repair-services" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-950">{service.name}</h1>
          <p className="text-sm text-slate-500">
            {isPerModel ? 'Edit service info and per-device pricing' : 'Scoped flat pricing — one price across matching devices'}
          </p>
        </div>
      </div>

      <div className="flex min-h-[70vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <aside className="w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50">
          <button
            type="button"
            className={`block w-full border-b border-slate-200 px-4 py-3 text-left text-sm font-semibold ${
              selected === 'info' ? 'bg-white text-primary' : 'text-slate-700 hover:bg-white/80'
            }`}
            onClick={() => setSelected('info')}
          >
            Service Information
          </button>
          {isPerModel ? (
            <>
              {activePricing.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`flex w-full items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 text-left text-sm ${
                    selected === row.id ? 'bg-white font-semibold text-primary' : 'text-slate-700 hover:bg-white/80'
                  }`}
                  onClick={() => setSelected(row.id)}
                >
                  <span className="min-w-0 truncate">
                    <span className="text-green-600">✓ </span>
                    {row.modelName ?? row.makeName}
                  </span>
                  <span className="shrink-0 text-xs text-slate-500">{formatCurrency(row.price)}</span>
                </button>
              ))}
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-primary hover:bg-white"
                onClick={() => setAddOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add Model / Make
              </button>
            </>
          ) : (
            <div className="px-4 py-3 text-xs text-slate-500">
              Per-model pricing matrix is hidden for scoped services. Switch scope to “Specific device models” to manage matrix prices.
            </div>
          )}
        </aside>

        <div className="min-w-0 flex-1 overflow-y-auto p-6">
          {selected === 'info' ? (
            <form
              className="max-w-xl space-y-6"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (!isPerModel && (flatPrice === '' || Number.isNaN(Number(flatPrice)))) {
                  toastError('Flat price is required for scoped services');
                  return;
                }
                saveInfoMut.mutate();
              }}
            >
              <h2 className="text-lg font-bold uppercase tracking-wide text-slate-500">Service Information</h2>
              <label className="block text-sm">
                <span className="font-medium">Service Name *</span>
                <input className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={infoName} onChange={(e) => setInfoName(e.target.value)} required />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Category</span>
                <select className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={infoCategory} onChange={(e) => setInfoCategory(e.target.value)}>
                  {REPAIR_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </label>

              <fieldset className="space-y-3 rounded-xl border border-slate-200 p-4">
                <legend className="px-1 text-sm font-bold uppercase tracking-wide text-slate-500">Applies To</legend>
                {(
                  [
                    { value: 'per_model' as const, label: 'Specific device models' },
                    { value: 'all_devices' as const, label: 'All devices' },
                    { value: 'categories' as const, label: 'Device types' },
                    { value: 'makes' as const, label: 'Specific manufacturers' },
                  ] as const
                ).map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="scope"
                      checked={scopeType === opt.value}
                      onChange={() => setScopeType(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}

                {scopeType === 'categories' ? (
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {DEVICE_SCOPE_CATEGORIES.map((c) => (
                      <label key={c.value} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={scopeCategories.includes(c.value)}
                          onChange={() => toggleCategory(c.value)}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                ) : null}

                {scopeType === 'makes' ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {scopeMakeIds.map((mid) => (
                        <span
                          key={mid}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-800"
                        >
                          {makeNameById.get(mid) ?? mid.slice(0, 8)}
                          <button
                            type="button"
                            className="text-slate-500 hover:text-red-600"
                            onClick={() => setScopeMakeIds((ids) => ids.filter((x) => x !== mid))}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        value={addScopeMakeId}
                        onChange={(e) => setAddScopeMakeId(e.target.value)}
                      >
                        <option value="">Add manufacturer…</option>
                        {(makesQuery.data ?? [])
                          .filter((m) => !scopeMakeIds.includes(m.id))
                          .map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                        disabled={!addScopeMakeId}
                        onClick={() => {
                          if (addScopeMakeId) {
                            setScopeMakeIds((ids) => [...ids, addScopeMakeId]);
                            setAddScopeMakeId('');
                          }
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">Optionally narrow by type:</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {DEVICE_SCOPE_CATEGORIES.map((c) => (
                        <label key={c.value} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={scopeCategories.includes(c.value)}
                            onChange={() => toggleCategory(c.value)}
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}
              </fieldset>

              {isPerModel ? (
                <label className="block text-sm">
                  <span className="font-medium">Default Price</span>
                  <span className="block text-xs text-slate-500">Fallback when no model-specific price is set</span>
                  <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={infoDefaultPrice} onChange={(e) => setInfoDefaultPrice(e.target.value)} />
                </label>
              ) : (
                <fieldset className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <legend className="px-1 text-sm font-bold uppercase tracking-wide text-slate-500">Pricing</legend>
                  <label className="block text-sm">
                    <span className="font-medium">Price *</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                      value={flatPrice}
                      onChange={(e) => setFlatPrice(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    Est. time (minutes)
                    <input
                      type="number"
                      min="1"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                      value={flatMinutes}
                      onChange={(e) => setFlatMinutes(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    Warranty (days)
                    <input
                      type="number"
                      min="0"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                      value={flatWarranty}
                      onChange={(e) => setFlatWarranty(e.target.value)}
                    />
                  </label>
                </fieldset>
              )}

              {!isPerModel ? (
                <fieldset className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <legend className="px-1 text-sm font-bold uppercase tracking-wide text-slate-500">Exclude Models</legend>
                  <p className="text-xs text-slate-500">
                    This service will not be offered for these models even though they match the scope above.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {scopeExcludedModelIds.map((mid) => (
                      <span
                        key={mid}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900"
                      >
                        {mid.slice(0, 8)}…
                        <button
                          type="button"
                          onClick={() =>
                            setScopeExcludedModelIds((ids) => ids.filter((x) => x !== mid))
                          }
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <select
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      value={excludeMakeId}
                      onChange={(e) => {
                        setExcludeMakeId(e.target.value);
                        setExcludeModelId('');
                      }}
                    >
                      <option value="">Brand…</option>
                      {(makesQuery.data ?? []).map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    <select
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      value={excludeModelId}
                      onChange={(e) => setExcludeModelId(e.target.value)}
                      disabled={!excludeMakeId}
                    >
                      <option value="">Model…</option>
                      {(excludeModelsQuery.data ?? []).map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="text-sm font-semibold text-primary disabled:opacity-50"
                    disabled={!excludeModelId || scopeExcludedModelIds.includes(excludeModelId)}
                    onClick={() => {
                      if (excludeModelId) {
                        setScopeExcludedModelIds((ids) => [...ids, excludeModelId]);
                        setExcludeModelId('');
                      }
                    }}
                  >
                    + Add exclusion
                  </button>
                </fieldset>
              ) : null}

              {!isPerModel ? (
                <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <p className="font-semibold">
                    Preview — Applies to{' '}
                    {(scopePreviewQuery.data?.matchingModelCount ?? 0).toLocaleString()} device models
                  </p>
                  {scopePreviewQuery.isFetching ? (
                    <p className="text-xs text-slate-500">Updating count…</p>
                  ) : null}
                </div>
              ) : null}

              <label className="block text-sm">
                <span className="font-medium">Sort Order</span>
                <input type="number" className="mt-1 w-32 rounded-xl border border-slate-300 px-3 py-2" value={infoSortOrder} onChange={(e) => setInfoSortOrder(e.target.value)} />
              </label>
              <fieldset className="space-y-2">
                <legend className="text-sm font-bold uppercase text-slate-500">Display Settings</legend>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={infoDisplayPos} onChange={(e) => setInfoDisplayPos(e.target.checked)} /> Display on POS</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={infoDisplayPortal} onChange={(e) => setInfoDisplayPortal(e.target.checked)} /> Display on Customer Portal</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={infoDisplayWidget} onChange={(e) => setInfoDisplayWidget(e.target.checked)} /> Display on Widget / Display Screen</label>
              </fieldset>
              <fieldset className="space-y-2">
                <legend className="text-sm font-bold uppercase text-slate-500">Loyalty</legend>
                {(['inherit', 'custom_rate', 'disabled'] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="loyalty" checked={infoLoyalty === opt} onChange={() => setInfoLoyalty(opt)} />
                    {opt === 'inherit' ? 'Inherit store default' : opt === 'custom_rate' ? 'Custom rate' : 'Disabled (no points)'}
                  </label>
                ))}
                {infoLoyalty === 'custom_rate' ? (
                  <input className="w-32 rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="pts per $" value={infoLoyaltyRate} onChange={(e) => setInfoLoyaltyRate(e.target.value)} />
                ) : null}
              </fieldset>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={infoActive} onChange={(e) => setInfoActive(e.target.checked)} />
                Active
              </label>
              <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={saveInfoMut.isPending}>
                Save Service Info
              </button>
            </form>
          ) : selectedPricing && isPerModel ? (
            <form
              className="max-w-xl space-y-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                saveModelMut.mutate();
              }}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-950">{selectedPricing.modelName ?? selectedPricing.makeName}</h2>
                <button type="button" className="rounded-lg p-2 text-red-600 hover:bg-red-50" onClick={() => removeModelMut.mutate(selectedPricing.id)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-slate-500">
                Brand: {selectedPricing.makeName ?? '—'} · Model: {selectedPricing.modelName ?? 'All models'}
              </p>
              <fieldset className="space-y-3">
                <legend className="text-sm font-bold uppercase text-slate-500">Repair Charges</legend>
                <label className="block text-sm">
                  <span className="font-medium">Retail Price *</span>
                  <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={modelPrice} onChange={(e) => setModelPrice(e.target.value)} required />
                </label>
                <label className="block text-sm">
                  <span className="font-medium">Sale Price</span>
                  <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={modelSalePrice} onChange={(e) => setModelSalePrice(e.target.value)} />
                </label>
              </fieldset>
              <fieldset className="space-y-3">
                <legend className="text-sm font-bold uppercase text-slate-500">Additional Details</legend>
                <label className="block text-sm">
                  Estimated Time (minutes)
                  <input type="number" min="1" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={modelMinutes} onChange={(e) => setModelMinutes(e.target.value)} />
                </label>
                <label className="block text-sm">
                  Warranty (days)
                  <input type="number" min="0" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={modelWarranty} onChange={(e) => setModelWarranty(e.target.value)} />
                </label>
                <label className="block text-sm">
                  Description
                  <textarea className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2" value={modelDescription} onChange={(e) => setModelDescription(e.target.value)} />
                </label>
              </fieldset>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={modelActive} onChange={(e) => setModelActive(e.target.checked)} />
                Active
              </label>
              <div className="flex gap-2">
                <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={saveModelMut.isPending}>
                  Save Changes
                </button>
                <button type="button" className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-700" onClick={() => removeModelMut.mutate(selectedPricing.id)}>
                  Remove Model
                </button>
              </div>
            </form>
          ) : (
            <p className="text-slate-500">Select a model from the left panel.</p>
          )}
        </div>
      </div>

      {addOpen && isPerModel ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold">Add Device Pricing</h2>
            <label className="mt-4 flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)} />
              Use for multiple models
            </label>
            <label className="mt-3 block text-sm">
              Brand
              <select className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addMakeId} onChange={(e) => { setAddMakeId(e.target.value); setSelectedModelIds([]); setAddModelId(''); }}>
                <option value="">Select brand…</option>
                {(makesQuery.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
            {bulkMode && addMakeId ? (
              <div className="mt-3 max-h-48 overflow-y-auto rounded-xl border border-slate-200 p-2">
                <div className="mb-2 flex gap-2">
                  <button type="button" className="text-xs font-semibold text-primary" onClick={() => setSelectedModelIds((modelsQuery.data ?? []).map((m) => m.id))}>Select All</button>
                  <button type="button" className="text-xs font-semibold text-slate-500" onClick={() => setSelectedModelIds([])}>Deselect All</button>
                </div>
                {(modelsQuery.data ?? []).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 py-1 text-sm">
                    <input type="checkbox" checked={selectedModelIds.includes(m.id)} onChange={() => toggleModel(m.id)} />
                    {m.name}
                  </label>
                ))}
              </div>
            ) : !bulkMode && addMakeId ? (
              <label className="mt-3 block text-sm">
                Model
                <select className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addModelId} onChange={(e) => setAddModelId(e.target.value)}>
                  <option value="">All models of this brand</option>
                  {(modelsQuery.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                Price *
                <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addPrice} onChange={(e) => setAddPrice(e.target.value)} />
              </label>
              <label className="block text-sm">
                Sale Price
                <input type="number" min="0" step="0.01" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addSalePrice} onChange={(e) => setAddSalePrice(e.target.value)} />
              </label>
              <label className="block text-sm">
                Est. minutes
                <input type="number" min="1" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addMinutes} onChange={(e) => setAddMinutes(e.target.value)} />
              </label>
              <label className="block text-sm">
                Warranty days
                <input type="number" min="0" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" value={addWarranty} onChange={(e) => setAddWarranty(e.target.value)} />
              </label>
            </div>
            {bulkMode ? (
              <p className="mt-2 text-sm text-slate-500">Selected: {selectedModelIds.length} models</p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold" onClick={() => setAddOpen(false)}>Cancel</button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!addPrice || addPricingMut.isPending || (bulkMode ? selectedModelIds.length === 0 : !addMakeId)}
                onClick={() => addPricingMut.mutate()}
              >
                {bulkMode ? `Add ${selectedModelIds.length} Models →` : 'Add Model →'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
